import Head from 'next/head';
import { useRouter } from 'next/router';
import { gql } from '@apollo/client';
import { useQuery } from '@helpers/async';
import { initializeApollo, addApolloState } from '../../apollo-client';
import NextNprogress from 'nextjs-progressbar';

import Layout from '../../components/Layout';
import Hero from '../../components/Home/Hero';
import SquarePattern from '../../components/UI/SquarePattern';
import PressCards from '../../components/UI/PressCards';
import MasonryCard from '../../components/UI/MasonryCard';

import metaRobots from '../../helpers/metaRobots';

import sharecardImage from '../../public/images/sharecard.jpg';
import { Container, Box, Grid, GridItem, Heading } from '@chakra-ui/react';
import sitePath from '../../helpers/sitePath';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

const hero = {
  longText: false,
  smallText: true,
  withPattern: true,
  title: {
    main: 'Press',
    highlight: 'room.',
    color: '',
  },
  text: 'Stay up to date on company news and see how Terminal is transforming the remote tech talent landscape with our latest media coverage.',
};

const masonryCards = [
  {
    type: 'content_image',
    position: true,
    bgDark: false,
    image: {
      sourceUrl: '/images/press/terminal-primary-logo.svg',
      altText: 'Terminal logo',
    },
    title: 'Short description',
    text: [
      {
        strong: '',
        normal:
          'Terminal is the hiring and employment platform for building remote engineering teams.',
      },
    ],
    button: [
      {
        text: {
          normal: 'Download',
          strong: 'Terminal Logos',
        },
        url: '/brand-assets/Terminal-Logos.zip',
      },
    ],
  },
  {
    type: 'content_image',
    position: false,
    bgDark: false,
    image: {
      sourceUrl: '/images/press/terminal-offices.jpg',
      altText: 'Two engineers work together in a co-working space',
    },
    title: 'Boilerplate description',
    text: [
      {
        strong: '',
        normal:
          'Terminal is the leading platform used by companies to build remote engineering teams faster than any other solution on the market. The company provides tech-enabled hiring, employment and people-management solutions to build and run world-class remote engineering teams. Terminal has hired more than 700 engineers and built teams for top tech companies such as Hims and Hers, Chime, Everly Health and Skillz. Learn more about remote scaling at Terminal.io.',
      },
    ],
    button: [
      {
        text: {
          normal: 'Download',
          strong: 'Terminal office photos',
        },
        url: '/brand-assets/Terminal-OfficePhotos.zip',
      },
    ],
  },
];

const footer = {
  longText: false,
  text: 'Find the scale you need. Build your team today.',
};

const GET_PRESS = gql`
  query GetPostsEdges {
    pressReleases(first: 1000) {
      nodes {
        date
        slug
        title
      }
    }
    news(first: 1000) {
      nodes {
        date
        title
        newsGroup {
          news {
            logo {
              sourceUrl
            }
            publication
            url
          }
        }
      }
    }
    press(first: 1000) {
      nodes {
        date
        title
        pressGroup {
          pressRelease {
            url
          }
        }
      }
    }
  }
`;

export async function getServerSideProps({ locale }) {
  const apolloClient = initializeApollo();

  await apolloClient.query({
    query: GET_PRESS,
  });

  return addApolloState(apolloClient, {
    props: {
      ...(await serverSideTranslations(locale)),
    },
  });
}

export default function Press() {
  const router = useRouter();

  // Client side Apollo
  const { loading, data } = useQuery(GET_PRESS);

  // CSR loading
  if (loading) {
    return (
      <NextNprogress
        color="#25383F"
        startPosition={0.3}
        stopDelayMs={200}
        height={3}
        showOnShallow={true}
        options={{
          showSpinner: false,
        }}
      />
    );
  }

  const news = data?.news?.nodes ?? null;
  const press = data?.press?.nodes ?? null;
  const pressReleases = data?.pressReleases?.nodes ?? null;

  const meta = {
    title: 'Press - Terminal.io',
    description:
      'Stay up to date on company news and see how Terminal is transforming the remote tech talent landscape with our latest media coverage.',
  };

  return (
    <>
      <Head>
        <link rel="canonical" href={sitePath(router.asPath)} />
        <title>{meta.title}</title>
        <meta property="og:title" content={meta.title} />
        <meta name="description" content={meta.description} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={sitePath(router.asPath)} />
        <meta property="og:image" content={sharecardImage.src} />
        <meta name="robots" content={metaRobots({ translated: false, locale: router.locale })} />
      </Head>
      <Layout type="businesses" footer={footer} withCTA>
        {hero && (
          <Box bgColor="grey.100">
            <Hero hero={hero} />
          </Box>
        )}
        {pressReleases && pressReleases.length > 0 && (
          <>
            <Container mt={{ base: '28' }}>
              <Heading as="h2" fontSize={{ base: '2rem' }} lineHeight={{ base: '2.25rem' }}>
                Press Releases
              </Heading>
            </Container>
            <Box mt={{ base: '8' }}>
              <PressCards cards={pressReleases} />
            </Box>
          </>
        )}
        {news && news.length > 0 && (
          <>
            <Container mt={{ base: '28' }}>
              <Heading as="h2" fontSize={{ base: '2rem' }} lineHeight={{ base: '2.25rem' }}>
                News
              </Heading>
            </Container>
            <Box mt={{ base: '8' }}>
              <PressCards cards={news} />
            </Box>
          </>
        )}
        {press && press.length > 0 && (
          <>
            <Container mt={{ base: '28' }}>
              <Heading as="h2" fontSize={{ base: '2rem' }} lineHeight={{ base: '2.25rem' }}>
                Press
              </Heading>
            </Container>
            <Box mt={{ base: '8' }}>
              <PressCards cards={press} />
            </Box>
          </>
        )}
        {masonryCards && masonryCards.length && (
          <>
            <Container mt={{ base: '28' }}>
              <Heading as="h2" fontSize={{ base: '2rem' }} lineHeight={{ base: '2.25rem' }}>
                Press resources
              </Heading>
            </Container>
            <Grid gap={{ base: '14', lg: '6' }} mt={{ base: '12' }}>
              {masonryCards.slice(0, 4).map((card) => (
                <GridItem key={card.title}>
                  <MasonryCard card={card} />
                </GridItem>
              ))}
            </Grid>
            <SquarePattern mt={{ base: '-18.75rem', lg: '-14.5rem' }} />
          </>
        )}
        <Container>
          <Box bgColor="white" mt={{ base: '1.5rem', lg: '3.5rem' }} textAlign="center">
            <Box maxW="35rem" mx="auto">
              <Heading
                as="h2"
                color="grey.900"
                fontSize={{ base: '1.75rem', lg: '2rem' }}
                lineHeight={{ base: '2rem', lg: '2.25rem' }}
              >
                Have a media or speaking inquiry? Reach out at{' '}
                <a href="mailto:press@terminal.io" target="_blank" rel="noreferrer noopener">
                  press@terminal.io
                </a>
              </Heading>
            </Box>
          </Box>
        </Container>
      </Layout>
    </>
  );
}
